/**
 * rain-rings — raindrop rings on a WATER surface (2026-09-11, interactive
 * water), the same ring profile as render/materials/RainRipples (Lagarde's
 * "Water drop 2b": the plane cut into cells, one drop per cell at a random
 * phase, a ring whose radius grows while its amplitude decays; four cell
 * layers so no lattice shows). That layer perturbs a lit material's
 * view-space normalNode; the water material is UNLIT and composes its own
 * world-space normal, so this variant returns the rings' world-XZ slope for
 * the water's normal sum. Kept here (not imported across the subsystem
 * boundary) with the profile constants pinned to RainRipples.rippleHeightCpu.
 */
import { float, vec2, floor, fract, time, sin, exp, saturate } from 'three/tsl';

type FloatNode = ReturnType<typeof float>;
type Vec2Node = ReturnType<typeof vec2>;

/** Ring profile constants — the same as RainRipples.rippleHeightCpu. */
export const RAIN_RING_SPEED = 0.55;
export const RAIN_RING_LAYERS: ReadonlyArray<readonly [number, number]> = [[1, 0], [1.37, 0.31], [0.71, 0.63], [1.91, 0.87]];

/**
 * Per-cell hash, the sign-safe sin/fract form RainRipples uses. WHY not
 * three's `hash()`: it converts its float seed with `toUint()` — WGSL
 * `u32(f32)` SATURATES negatives to 0 (GLSL `uint()` of a negative is
 * undefined) and drops the fraction — so every cell on the −X/−Z side of the
 * rebased origin hashed to the same constant: one drop position, one phase,
 * a lattice of rings pulsing in lock-step across half the sea (2026-09-12).
 */
export function rainRingHashCpu(ix: number, iy: number, salt: number): number {
  const s = Math.sin(ix * 127.1 + iy * 311.7 + salt) * 43758.5453;
  return s - Math.floor(s);
}
function rainRingHash(ix: FloatNode, iy: FloatNode, salt: number): FloatNode {
  return fract(ix.mul(127.1).add(iy.mul(311.7)).add(salt).sin().mul(43758.5453));
}

/**
 * World-XZ slope (dh/dx, dh/dz) of the rain rings at `pxz`, scaled by
 * `uIntensity` (0..1) × `strength`. Add to a normal as (−sx, 1, −sz).
 */
export function rainRingSlope(pxz: Vec2Node, uIntensity: FloatNode, strength = 0.35, cellSize = 0.35, rate = 1.4): { x: FloatNode; z: FloatNode } {
  const gx = float(0).toVar();
  const gz = float(0).toVar();
  for (const [scale, phaseOff] of RAIN_RING_LAYERS) {
    const cell = cellSize * scale;
    const p = pxz.div(cell);
    const id = floor(p);
    const h = rainRingHash(id.x, id.y, phaseOff * 113);
    const h2 = rainRingHash(id.y, id.x, phaseOff * 71 + 17.3);
    // The drop lands somewhere in its cell.
    const centre = id.add(vec2(h, h2).mul(0.6).add(0.2));
    const rel = p.sub(centre);
    const r = rel.length();
    const t = fract(time.mul(rate).add(h.mul(7)).add(phaseOff));
    const radius = t.mul(RAIN_RING_SPEED);
    const d = r.sub(radius);
    const env = exp(t.mul(-4)).mul(float(1).sub(t));
    const g = sin(d.mul(-60)).mul(exp(d.mul(d).mul(-400))).mul(env);
    const slope = g.mul(float(60)).mul(saturate(float(1).sub(r.mul(2.2))));
    const dir = rel.div(r.max(1e-4));
    gx.addAssign(dir.x.mul(slope));
    gz.addAssign(dir.y.mul(slope));
  }
  const k = uIntensity.mul(strength * 0.02); // intensity applied ONCE (RainRipples' lesson)
  return { x: gx.mul(k), z: gz.mul(k) };
}
